class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHtml = parsedState?.sections?.[section.id];
      if (!sectionHtml) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    window.armoniaSyncCartBadge?.();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// =========================================================
// ARMONIA CART FIX — 07 SEP 2026
// 1) Always show the real cart item count on the header icon.
// 2) Force the cart drawer to open from the right on desktop + mobile.
// =========================================================

(function initArmoniaCartFix() {
  if (!document.getElementById('armonia-cart-right-fix')) {
    const style = document.createElement('style');
    style.id = 'armonia-cart-right-fix';
    style.textContent = `
      cart-drawer.drawer {
        direction: ltr !important;
        justify-content: flex-end !important;
      }

      #CartDrawer.cart-drawer {
        position: absolute !important;
        inset: 0 !important;
        width: 100% !important;
        height: 100% !important;
        pointer-events: none !important;
      }

      #CartDrawer .cart-drawer__overlay {
        pointer-events: auto !important;
      }

      #CartDrawer .drawer__inner {
        position: absolute !important;
        top: 0 !important;
        right: 0 !important;
        left: auto !important;
        margin-left: auto !important;
        margin-right: 0 !important;
        pointer-events: auto !important;
        transform: translateX(100%) !important;
      }

      cart-drawer.drawer.active #CartDrawer .drawer__inner {
        transform: translateX(0) !important;
      }

      #cart-icon-bubble {
        position: relative;
      }
    `;
    document.head.appendChild(style);
  }

  const setBadge = (count) => {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (!cartIcon) return;

    let bubble = cartIcon.querySelector('.cart-count-bubble');
    const itemCount = Math.max(0, Number(count) || 0);

    if (itemCount === 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      cartIcon.appendChild(bubble);
    }

    const visibleCount = bubble.querySelector('[aria-hidden="true"]');
    const accessibleCount = bubble.querySelector('.visually-hidden');

    if (visibleCount) visibleCount.textContent = itemCount > 99 ? '99+' : String(itemCount);
    if (accessibleCount) accessibleCount.textContent = `${itemCount} proizvoda u korpi`;
  };

  let syncPromise = null;

  window.armoniaSyncCartBadge = async function armoniaSyncCartBadge() {
    if (syncPromise) return syncPromise;

    const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;

    syncPromise = fetch(cartUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart count request failed');
        return response.json();
      })
      .then((cart) => setBadge(cart?.item_count || 0))
      .catch((error) => console.warn('Armonia cart badge sync failed.', error))
      .finally(() => {
        syncPromise = null;
      });

    return syncPromise;
  };

  const subscribeToCartUpdates = () => {
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          window.setTimeout(() => window.armoniaSyncCartBadge?.(), 0);
        });
        return true;
      }
    } catch (error) {
      console.warn('Armonia cart badge subscription failed.', error);
    }
    return false;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.armoniaSyncCartBadge?.(), { once: true });
  } else {
    window.armoniaSyncCartBadge?.();
  }

  if (!subscribeToCartUpdates()) {
    window.addEventListener('load', subscribeToCartUpdates, { once: true });
  }
})();
